import React from 'react';
import { Badge } from './ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { Users, Wifi, WifiOff } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

export const OnlineUsers: React.FC = () => {
  const { onlineUsers, isOnline } = useAuth();

  const getDeviceIcon = (device: string) => {
    switch (device.toLowerCase()) {
      case 'mobile': return '📱';
      case 'tablet': return '📱';
      case 'mac': return '💻';
      default: return '🖥️';
    }
  };

  return (
    <div className="flex items-center space-x-2">
      {/* Network Status */}
      <Badge variant={isOnline ? "default" : "destructive"} className="text-xs">
        {isOnline ? (
          <>
            <Wifi className="w-3 h-3 mr-1" />
            Online
          </>
        ) : (
          <>
            <WifiOff className="w-3 h-3 mr-1" />
            Offline
          </>
        )}
      </Badge>

      {/* Online Users */}
      {onlineUsers.length > 0 && (
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-8 px-2">
              <Users className="w-3 h-3 mr-1" />
              <div className="flex -space-x-1 mr-1">
                {onlineUsers.slice(0, 3).map((user, index) => (
                  <span 
                    key={user.uid} 
                    className="text-sm"
                    style={{ zIndex: 10 - index }}
                  >
                    {user.emoji}
                  </span>
                ))}
              </div>
              <span className="text-xs">{onlineUsers.length}</span>
            </Button>
          </PopoverTrigger>
          
          <PopoverContent align="end" className="w-80 p-0">
            <div className="p-4 border-b">
              <h4 className="font-medium text-sm">Online Users ({onlineUsers.length})</h4>
              <p className="text-xs text-muted-foreground mt-1">
                Lab staff currently active in the system
              </p>
            </div>
            
            <div className="max-h-64 overflow-y-auto">
              {onlineUsers.map((user) => (
                <div key={user.uid} className="flex items-center space-x-3 p-3 hover:bg-muted/50">
                  <div className="flex items-center space-x-2">
                    <span className="text-lg">{user.emoji}</span>
                    <span className="text-sm">{getDeviceIcon(user.device)}</span>
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {user.displayName}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {user.email}
                    </p>
                  </div>
                  
                  <div className="text-right">
                    <Badge variant="outline" className="text-xs">
                      {user.device}
                    </Badge>
                    <p className="text-xs text-muted-foreground mt-1">
                      Active now
                    </p>
                  </div>
                </div>
              ))}
            </div>
            
            {onlineUsers.length === 0 && (
              <div className="p-6 text-center text-muted-foreground">
                <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No users online</p>
              </div>
            )}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};